import { C } from "../constants";
import { Sec } from "../components/Shared";

const h2Style = {
  fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 22,
  color: C.text, margin: "40px 0 16px", lineHeight: 1.3
};
const h3Style = {
  fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 17,
  color: C.text, margin: "28px 0 10px", lineHeight: 1.3
};
const pStyle = {
  fontFamily: "'Nunito', sans-serif", fontSize: 15, color: C.textMid,
  lineHeight: 1.75, margin: "0 0 14px"
};
const liStyle = {
  fontFamily: "'Nunito', sans-serif", fontSize: 15, color: C.textMid,
  lineHeight: 1.75, marginBottom: 6
};
const linkStyle = { color: C.blue, fontWeight: 600, textDecoration: "none" };

const thStyle = {
  fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 13.5,
  color: C.text, padding: "10px 14px", textAlign: "left",
  borderBottom: `2px solid ${C.border}`, background: C.blueLight
};
const tdStyle = {
  fontFamily: "'Nunito', sans-serif", fontSize: 13.5, color: C.textMid,
  padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
  lineHeight: 1.6, verticalAlign: "top"
};

function DataTable({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 20 }}>
      <table style={{
        width: "100%", borderCollapse: "collapse", borderRadius: 10,
        border: `1px solid ${C.border}`, overflow: "hidden"
      }}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={thStyle}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
              {row.map((cell, j) => <td key={j} style={tdStyle}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Privacy() {
  return (
    <Sec>
      <div style={{ maxWidth: 740, margin: "0 auto" }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.blueLight}, ${C.bluePale})`,
          borderRadius: 16, padding: "32px 36px", marginBottom: 36,
          border: `1px solid ${C.border}`
        }}>
          <h1 style={{
            fontFamily: "'Nunito', sans-serif", fontWeight: 900,
            fontSize: "clamp(26px, 4vw, 34px)", color: C.text, margin: "0 0 12px"
          }}>SafeZapp Privacy Policy</h1>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 14, color: C.textMid, margin: "0 0 16px" }}>
            Last updated: May 2026
          </p>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10, background: C.white, borderRadius: 10, padding: "14px 18px",
            border: `1px solid ${C.border}`
          }}>
            {[
              ["App name", "SafeZapp"],
              ["Developed By", "Dogmatech Development Solutions, Australia."],
              ["Platform", "Android (Google Play) and iOS (App Store), Australia"],
              ["Contact", "support@safezapp.app"],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 12, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 600, fontSize: 14, color: C.text }}>
                  {label === "Contact" ? <a href="mailto:support@safezapp.app" style={linkStyle}>{value}</a> : value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 1 */}
        <h2 style={h2Style}>1. About SafeZapp</h2>
        <p style={pStyle}>SafeZapp is a supervised messaging app designed for people who need oversight of their messaging. It pairs each user with a trusted adult ("Approver") — such as a guardian, carer, or other trusted person — who oversees their contacts and messaging activity.</p>
        <p style={pStyle}>This privacy policy explains what personal information we collect, why we collect it, how we use and protect it, and your rights regarding your data.</p>

        {/* 2 */}
        <h2 style={h2Style}>2. Who This Policy Applies To</h2>
        <p style={pStyle}>SafeZapp has two types of accounts:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}><strong style={{ color: C.text }}>Approver accounts</strong> — adults (guardians, carers, or other trusted persons) who supervise one or more users. This is a paid account.</li>
          <li style={liStyle}><strong style={{ color: C.text }}>User accounts</strong> — individuals who use SafeZapp under the supervision of an Approver. This is a free account.</li>
        </ul>
        <p style={pStyle}>This policy applies to both account types.</p>

        {/* 3 */}
        <h2 style={h2Style}>3. Information We Collect</h2>

        <h3 style={h3Style}>3.1 Information you provide during registration</h3>
        <DataTable
          headers={["Data", "Purpose"]}
          rows={[
            ["Mobile phone number", "Account verification via SMS (handled by Firebase Authentication)"],
            ["Display name", "Identifies you to other users in the app (3–25 characters, filtered for inappropriate content)"],
            ["Account role", "Whether you are an \"Approver\" or a \"User\" — determines your permissions"],
          ]}
        />

        <h3 style={h3Style}>3.2 Information generated automatically</h3>
        <DataTable
          headers={["Data", "Purpose"]}
          rows={[
            ["User ID (UID)", "Unique identifier assigned by Firebase Authentication"],
            ["Account creation timestamp", "Records when your account was created"],
            ["Last login timestamp", "Records when you last signed in"],
            ["FCM push notification token", "Enables delivery of push notifications (e.g. new messages, link requests)"],
            ["Privacy policy acceptance timestamp", "Records when you accepted this privacy policy"],
          ]}
        />

        <h3 style={h3Style}>3.3 Information generated through use of the app</h3>
        <DataTable
          headers={["Data", "Purpose"]}
          rows={[
            ["Chat messages", "Text messages sent between approved contacts"],
            ["Message metadata", "Timestamps, delivery status, and read receipts"],
            ["Contact lists", "Records of approved contacts and pending contact requests"],
            ["Link request records", "Records of Approver–User pairing requests, including status"],
            ["Pairing codes", "Temporary 6-digit codes used to link Approver and User accounts (valid for 15 minutes)"],
            ["Age bracket declaration", "The Approver declares the User's age bracket (under 13, 13–17, or 18+) when accepting a link request"],
            ["Relationship declaration", "The Approver confirms their relationship to the User (e.g. guardian, carer, or trusted person) when accepting a link request"],
            ["Consent timestamp", "Records when the Approver provided the age bracket and relationship declarations"],
            ["Subscription and trial data", "Manages subscription status, trial periods, and access entitlements"],
            ["Audit records", "Internal records of significant account events for compliance and safety purposes"],
          ]}
        />

        <h3 style={h3Style}>3.4 Information we do NOT collect</h3>
        <p style={pStyle}>We do not collect the following:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Date of birth or exact age</li>
          <li style={liStyle}>Email address</li>
          <li style={liStyle}>Real name (only a chosen display name)</li>
          <li style={liStyle}>Location or GPS data</li>
          <li style={liStyle}>Photos, videos, or media files</li>
          <li style={liStyle}>Device identifiers or advertising IDs</li>
          <li style={liStyle}>Browsing history</li>
          <li style={liStyle}>Contacts from your phone's address book</li>
        </ul>

        {/* 4 */}
        <h2 style={h2Style}>4. How We Use Your Information</h2>
        <p style={pStyle}>We use your personal information for the following purposes only:</p>
        <DataTable
          headers={["Purpose", "Legal basis"]}
          rows={[
            ["Creating and managing your account", "Necessary for the performance of the service"],
            ["Verifying your phone number via SMS", "Necessary for account security"],
            ["Delivering chat messages between approved contacts", "Necessary for the performance of the service"],
            ["Sending push notifications (new messages, link requests, account events)", "Necessary for the performance of the service"],
            ["Analysing message content for safety using automated systems", "Legitimate interest in user safety"],
            ["Enforcing the Approver supervision model (blocking messaging until an Approver is linked)", "Legitimate interest in user safety"],
            ["Managing Approver subscriptions and free trials", "Necessary for the performance of the service"],
            ["Enforcing display name rules (profanity filtering, 90-day change cooldown)", "Legitimate interest in maintaining a safe environment"],
            ["Automatic cleanup of expired data (see section 7)", "Legitimate interest in data minimisation"],
          ]}
        />
        <p style={pStyle}>We do not use your data for:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Advertising or marketing</li>
          <li style={liStyle}>Profiling or behavioural tracking</li>
          <li style={liStyle}>Sale to third parties</li>
          <li style={liStyle}>Automated decision-making that produces legal or similarly significant effects</li>
        </ul>

        {/* 5 */}
        <h2 style={h2Style}>5. How the Approver Supervision Model Works</h2>
        <p style={pStyle}>SafeZapp is built around the principle that users should only communicate under the oversight of a trusted adult. Here is how this works:</p>

        <h3 style={h3Style}>5.1 Before an account is created</h3>
        <p style={pStyle}>A User account cannot be created until the User enters a valid pairing code during registration. This code is generated by the Approver from their dashboard. No personal data (other than the phone number used for SMS verification via Firebase Authentication) is stored until the pairing code is validated and registration is complete.</p>

        <h3 style={h3Style}>5.2 The linking process</h3>
        <p style={pStyle}>Linking is done exclusively via pairing codes during registration:</p>
        <div style={{
          background: C.bg, borderRadius: 12, padding: "16px 20px", marginBottom: 14,
          border: `1px solid ${C.border}`
        }}>
          <p style={{ ...pStyle, margin: "0 0 10px" }}><strong style={{ color: C.text }}>Pairing code:</strong> The Approver generates a 6-digit pairing code from their dashboard. The code is valid for 15 minutes. During registration, the User enters this code before their account is created. A link request is automatically sent to the Approver. No phone numbers are shared during the pairing process. After linking, the Approver can view the User's registered phone number.</p>
        </div>
        <p style={pStyle}>When an Approver accepts a link request, they must:</p>
        <ol style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Declare the User's age bracket (under 13, 13–17, or 18+)</li>
          <li style={liStyle}>Confirm their relationship to the User (e.g. guardian, carer, or trusted person)</li>
        </ol>
        <p style={pStyle}>These declarations are recorded with a timestamp for compliance purposes.</p>

        <h3 style={h3Style}>5.3 After linking</h3>
        <p style={pStyle}>Once linked:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>The User can request new contacts, but every contact must be approved by the Approver before a chat can begin</li>
          <li style={liStyle}>The Approver can view all messages in supervised chats</li>
          <li style={liStyle}>The Approver can unlink the User at any time, which freezes the User's chats</li>
          <li style={liStyle}>Users can remove their own contacts (except their Approver contact), which freezes the associated chat</li>
          <li style={liStyle}>The Approver can remove contacts from their supervised Users' contact lists</li>
          <li style={liStyle}>If the Approver's subscription expires, the User cannot send messages</li>
          <li style={liStyle}>If a User is unlinked and does not re-link to an Approver within 14 days, their account and all associated data are automatically deleted (see section 7)</li>
        </ul>

        {/* 6 */}
        <h2 style={h2Style}>6. Third-Party Services</h2>
        <p style={pStyle}>SafeZapp uses the following third-party services to operate:</p>
        <DataTable
          headers={["Service", "Provider", "Purpose"]}
          rows={[
            ["Firebase Authentication", "Google LLC (USA)", "Phone number verification and account management"],
            ["Cloud Firestore", "Google LLC (USA)", "Storing user data, messages, contacts, and requests"],
            ["Firebase Cloud Functions", "Google LLC (USA)", "Server-side logic, enforcement of safety rules, scheduled data cleanup"],
            ["Firebase Cloud Messaging (FCM)", "Google LLC (USA)", "Push notifications"],
            ["AI Content Safety Service", "OpenAI Inc (USA)", "Automated safety analysis of message content to detect inappropriate or harmful material"],
          ]}
        />
        <p style={pStyle}>For details on how Google handles data, see the <a href="https://policies.google.com/privacy" style={linkStyle} target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>. For details on how OpenAI handles data, see the <a href="https://openai.com/privacy" style={linkStyle} target="_blank" rel="noopener noreferrer">OpenAI Privacy Policy</a>.</p>
        <p style={pStyle}>We do not share your data with any third parties other than those listed above, and only for the purposes stated.</p>

        {/* 7 */}
        <h2 style={h2Style}>7. Data Retention and Deletion</h2>
        <p style={pStyle}>We follow a data minimisation approach. Your data is retained only as long as necessary and is automatically cleaned up on the following schedule:</p>
        <DataTable
          headers={["Data type", "Retention period", "Cleanup method"]}
          rows={[
            ["Unlinked User accounts (no Approver linked)", "14 days after creation", "Automatically deleted, including Firebase Auth account; phone number is freed for re-use"],
            ["Archived account data (after you delete your account)", "90 days, then permanently deleted", "Scheduled weekly cleanup"],
            ["Cancelled user profile records", "90 days after cancellation, then permanently deleted", "Scheduled weekly cleanup"],
            ["Soft-deleted messages", "30 days, then permanently deleted", "Scheduled daily cleanup"],
            ["Expired or declined requests (contact requests, link requests)", "30 days, then permanently deleted", "Scheduled daily cleanup"],
            ["Pending link requests (never accepted or declined)", "14 days, then permanently deleted", "Scheduled daily cleanup"],
            ["All messages (regardless of status)", "365 days (1 year), then permanently deleted", "Scheduled weekly cleanup"],
            ["Expired pairing codes", "Deleted within 1 hour of expiry", "Scheduled hourly cleanup"],
            ["Push notification tokens (FCM)", "Cleared as soon as Google reports the token is no longer valid", "Cleared automatically on send failure"],
            ["Lapsed Approver accounts (no active subscription for 12+ months and no linked Users)", "Account fully purged following the standard cancellation flow", "Scheduled weekly cleanup"],
            ["Internal audit records", "2 years, then permanently deleted", "Scheduled monthly cleanup"],
          ]}
        />

        <h3 style={h3Style}>7.1 Deleting your account</h3>
        <p style={pStyle}>You can delete your account at any time from the Profile screen ("Cancel my account"). When you do:</p>
        <ol style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>A two-step confirmation explains the consequences</li>
          <li style={liStyle}>Your account data is archived (not immediately deleted) for 90 days</li>
          <li style={liStyle}>Your Firebase Authentication account is deleted (you can no longer log in)</li>
          <li style={liStyle}>If you are an Approver, all supervised Users are unlinked</li>
          <li style={liStyle}>An audit record of the deletion is created</li>
          <li style={liStyle}>After 90 days, all archived data is permanently deleted, and your user profile record is itself permanently deleted</li>
        </ol>

        <h3 style={h3Style}>7.2 What happens in other people's chats</h3>
        <p style={pStyle}>Conversations on SafeZapp are shared between participants: each message you send also forms part of the chat history of the person you sent it to. When you delete your account:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>The chats you participated in are archived to your private archive for the 90-day window described above</li>
          <li style={liStyle}>The original messages remain visible in the other party's chat history, because that history is their record of the conversation (similar to how SMS or email threads remain on the recipient's device after the sender changes numbers)</li>
          <li style={liStyle}>The other party's chat list shows the chat as "Inactive" during the 90-day window, and as "Former user" once your profile is permanently deleted</li>
          <li style={liStyle}>New messages cannot be sent to your account once it is cancelled</li>
        </ul>
        <p style={pStyle}>If you require the other party to delete their copy of the messages you sent them, please contact them directly. We do not delete other users' chat history on your behalf.</p>

        <h3 style={h3Style}>7.3 Push notification tokens</h3>
        <p style={pStyle}>Push notification tokens (FCM tokens) are stored on your user profile so we can deliver notifications to your device. When Google reports that a token is no longer valid (for example, because you uninstalled the app or signed out), we clear the token from your profile automatically.</p>

        <h3 style={h3Style}>7.4 Lapsed Approver accounts</h3>
        <p style={pStyle}>If you are an Approver and your subscription has been inactive for at least 12 months, and you have no Users currently linked to your account, your account is treated as no longer needed and will be purged following the same flow as a self-service cancellation. This is in line with APP 11.2, ensuring that long-abandoned paid accounts do not retain personal information indefinitely.</p>

        {/* 8 */}
        <h2 style={h2Style}>8. Data Security</h2>
        <p style={pStyle}>We take the following measures to protect your data:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>All data is transmitted over encrypted connections (HTTPS/TLS)</li>
          <li style={liStyle}>Personal data including display names, phone numbers, and message content is encrypted at rest using industry-standard encryption</li>
          <li style={liStyle}>Data is stored in Google Firebase's secure infrastructure</li>
          <li style={liStyle}>Safety restrictions (messaging blocks, contact approval) are enforced server-side and cannot be bypassed by the app</li>
          <li style={liStyle}>Display names are filtered for inappropriate content</li>
          <li style={liStyle}>Pairing codes expire after 15 minutes and are single-use</li>
          <li style={liStyle}>Access to user data requires Firebase Authentication</li>
        </ul>

        {/* 9 */}
        <h2 style={h2Style}>9. User Privacy</h2>
        <p style={pStyle}>SafeZapp is designed for use by individuals under the supervision of an adult Approver. We take user privacy seriously:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}><strong style={{ color: C.text }}>Data minimisation:</strong> We collect only the minimum data necessary to operate the service (phone number, display name, role)</li>
          <li style={liStyle}><strong style={{ color: C.text }}>No data collection before consent:</strong> Push notification tokens are only activated after a User is linked to an Approver</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Automatic deletion:</strong> Unlinked accounts are automatically deleted after 14 days, ensuring that data for users who do not complete the linking process is not retained</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Approver oversight:</strong> All contacts must be approved by the Approver before messaging is enabled</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Content safety:</strong> All messages are subject to automated safety analysis to help maintain a safe messaging environment</li>
          <li style={liStyle}><strong style={{ color: C.text }}>No advertising or tracking:</strong> We do not serve ads, track behaviour, or profile users</li>
          <li style={liStyle}><strong style={{ color: C.text }}>No public profiles or discovery:</strong> Users cannot be found or contacted by anyone who does not have their phone number or a pairing code</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Age bracket declaration:</strong> Approvers must declare the User's age bracket, enabling age-appropriate handling</li>
        </ul>

        <h3 style={h3Style}>9.1 Australian Privacy Act 1988</h3>
        <p style={pStyle}>Under the Australian Privacy Act 1988, individuals under 13 are generally presumed to lack the capacity to consent to the collection of their personal information. SafeZapp addresses this through the Approver supervision model: an adult Approver must link with the user and declare their relationship before the user can access messaging features.</p>

        <h3 style={h3Style}>9.2 Children's Online Privacy Code (COPC)</h3>
        <p style={pStyle}>The Australian Children's Online Privacy Code is expected to come into effect by December 2026. SafeZapp is designed with the principles of the COPC in mind, including data minimisation, privacy by default, and age-appropriate design.</p>

        {/* 10 */}
        <h2 style={h2Style}>10. Your Rights</h2>
        <p style={pStyle}>You have the following rights regarding your personal information:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}><strong style={{ color: C.text }}>Access:</strong> You can view your account information within the app at any time</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Correction:</strong> You can update your display name (subject to a 90-day cooldown between changes)</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Deletion:</strong> You can delete your account at any time from the Profile screen, and all your data will be permanently removed within 90 days</li>
          <li style={liStyle}><strong style={{ color: C.text }}>Complaint:</strong> If you believe your privacy has been breached, you can contact us at <a href="mailto:support@safezapp.app" style={linkStyle}>support@safezapp.app</a> or lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" style={linkStyle} target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a></li>
        </ul>
        <p style={pStyle}>If you are under 18, your Approver may exercise these rights on your behalf.</p>

        {/* 11 */}
        <h2 style={h2Style}>11. Changes to This Policy</h2>
        <p style={pStyle}>We may update this privacy policy from time to time. If we make material changes, we will notify you through the app or by other appropriate means. The "Last updated" date at the top of this policy indicates when it was last revised.</p>
        <p style={pStyle}>Your continued use of SafeZapp after any changes constitutes your acceptance of the updated policy.</p>

        {/* 12 */}
        <h2 style={h2Style}>12. Contact Us</h2>
        <p style={pStyle}>If you have any questions about this privacy policy or how we handle your data, please contact us at:</p>
        <div style={{
          background: C.blueLight, borderRadius: 12, padding: "16px 20px",
          border: `1px solid ${C.border}`, marginBottom: 14
        }}>
          <p style={{ ...pStyle, margin: 0 }}>
            Email: <a href="mailto:support@safezapp.app" style={linkStyle}>support@safezapp.app</a><br />
            Website: <a href="https://safezapp.app" style={linkStyle}>https://safezapp.app</a>
          </p>
        </div>

        <div style={{
          background: C.bg, borderRadius: 10, padding: "14px 18px", marginTop: 20,
          border: `1px solid ${C.border}`
        }}>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13, color: C.textLight, margin: 0, fontStyle: "italic" }}>
            This privacy policy applies to the SafeZapp application distributed on the Google Play Store and Apple App Store in Australia.
          </p>
        </div>
      </div>
    </Sec>
  );
}
